import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createContext } from '@shared/utils/createContext';
import { JoyLogoLoaderBase } from './JoyLogoLoaderBase';

type FullPageLoaderActionsContextType = {
  stopTrackingKey: (key: string) => void;
  trackVisibilityForKey: (key: string, isLoading: boolean) => void;
};

type FullPageLoaderStateContextType = {
  isAnimationCompletelyStopped: boolean | undefined;
  shouldAnimate: boolean;
  setIsAnimationCompletelyStopped: (value: boolean) => void;
};

/**
 * The tracker callbacks and the animation flags are two separate contexts on purpose. The provider wraps the
 * whole app, and the flags flip every time any loader appears or disappears — merging them made every consumer
 * of the callbacks re-render on each flip (~680 components per flip, twice per admin page load).
 */
const [ActionsProvider, useFullPageLoaderActionsContext] = createContext<FullPageLoaderActionsContextType>({
  name: 'FullPageLoaderActions',
  strict: __BROWSER__,
  initialValue: {
    stopTrackingKey: () => {},
    trackVisibilityForKey: () => {},
  },
});

const [StateProvider, useFullPageLoaderStateContext] = createContext<FullPageLoaderStateContextType>({
  name: 'FullPageLoaderState',
  strict: __BROWSER__,
  initialValue: {
    isAnimationCompletelyStopped: undefined,
    shouldAnimate: false,
    setIsAnimationCompletelyStopped: () => {},
  },
});

const _checkIfLoadingForAnyKey = (tracker: Record<string, boolean>) => Object.entries(tracker).some(([_key, isEnabledForKey]) => isEnabledForKey);

/**
 * TODO:
 * Per Madhur:
 * can we log an aggregate telemetry event that captures all the loaders that were shown on a given screen. and when each of them started and ended.
 * that will help us track down which ui pieces are the long pole on complex pages. there should be just one event logged with all the loaders
 * (not one per loader) so that the telemetry itself doesnt flood the network
 */
export const FullPageLoaderProvider: React.FC = ({ children }) => {
  const [tracker, setTracker] = useState<Record<string, boolean>>({});
  const [isAnimationCompletelyStopped, setIsAnimationCompletelyStopped] = useState(_checkIfLoadingForAnyKey(tracker));

  const shouldAnimate = useMemo(() => {
    return _checkIfLoadingForAnyKey(tracker);
  }, [tracker]);

  useEffect(() => {
    if (shouldAnimate) {
      setIsAnimationCompletelyStopped(false);
    }
  }, [shouldAnimate]);

  const stopTrackingKey: FullPageLoaderActionsContextType['stopTrackingKey'] = useCallback(
    key => {
      // Copy before deleting, not after. `delete tracker[key]` would mutate the state object React
      // handed us, which is visible to anything still holding the previous state. React 17 renders
      // synchronously so it has no symptom today; under React 18 concurrent rendering it would.
      setTracker(tracker => {
        const nextTracker = { ...tracker };
        delete nextTracker[key];
        return nextTracker;
      });
    },
    [setTracker],
  );

  const trackVisibilityForKey: FullPageLoaderActionsContextType['trackVisibilityForKey'] = useCallback(
    (key, isLoading) => {
      setTracker(tracker => {
        return {
          ...tracker,
          [key]: isLoading,
        };
      });
    },
    [setTracker],
  );

  const actions = useMemo(() => {
    return { stopTrackingKey, trackVisibilityForKey };
  }, [stopTrackingKey, trackVisibilityForKey]);

  const state = useMemo(() => {
    return {
      // The reset effect above runs after commit, so the raw flag is still `true` for the render a loader starts in.
      isAnimationCompletelyStopped: shouldAnimate ? false : isAnimationCompletelyStopped,
      shouldAnimate,
      setIsAnimationCompletelyStopped,
    };
  }, [isAnimationCompletelyStopped, shouldAnimate, setIsAnimationCompletelyStopped]);

  return (
    <ActionsProvider value={actions}>
      <StateProvider value={state}>{children}</StateProvider>
    </ActionsProvider>
  );
};

/**
 * FullPageLoaderRenderer - Renders the full-page loader.
 */
export const FullPageLoaderRenderer: React.FC = () => {
  const { shouldAnimate, setIsAnimationCompletelyStopped } = useFullPageLoaderStateContext();

  if (!__BROWSER__) {
    return null;
  }

  return (
    <JoyLogoLoaderBase
      key="fullPageLoaderRenderer"
      shouldAnimate={shouldAnimate}
      onAnimateStop={() => setIsAnimationCompletelyStopped(true)}
    />
  );
};

type UseFullPageLoaderContextArgs = Readonly<{
  key: string;
  initialIsLoading?: boolean;
}>;

export const useFullPageLoader = (args: UseFullPageLoaderContextArgs) => {
  const { initialIsLoading = false, key } = args;
  const { stopTrackingKey, trackVisibilityForKey } = useFullPageLoaderActionsContext();
  const isMountedRef = useRef(false);

  const setIsLoading = useCallback(
    (isLoading: boolean) => {
      trackVisibilityForKey(key, isLoading);
    },
    [trackVisibilityForKey, key],
  );

  useEffect(() => {
    if (__BROWSER__ && !isMountedRef.current) {
      isMountedRef.current = true;
      if (initialIsLoading) {
        setIsLoading(initialIsLoading);
      }
    }
  }, [initialIsLoading, setIsLoading]);

  useEffect(() => {
    return () => {
      stopTrackingKey(key);
    };
  }, [stopTrackingKey, key]);

  return setIsLoading;
};

export { useFullPageLoaderActionsContext, useFullPageLoaderStateContext };
